<template>
  <div class="mt-1">
    <a-tooltip v-if="hasValidEmail && !showEmailInRow" placement="top" title="Send Email">
      <div class="quick_action_item" @click="sendEmail">
        <ThemeImage class="size-5" :icon="imageNames.EMAIL_GREY" alt="email_icon" />
      </div>
    </a-tooltip>

    <div v-if="showEmailInRow">
      <a-button
        class="secondary_small min-w-24 flex items-center justify-center gap-1"
        @click="accessEmail"
        :loading="isEnriching"
      >
        <ThemeImage
          v-if="!isEnriching"
          class="size-3 p-[1px] mb-[2px]"
          :icon="imageNames.WHITE_ENRICH_SEARCH"
          alt="enrich_search_icon"
        />
        <span class="font-semibold text-sm text_color">{{ buttonText }}</span>
      </a-button>
    </div>

    <CustomDropdown
      v-else-if="!hasValidEmail"
      ref="dropdownRef"
      gap="10px"
      position="end"
      :relative-to-viewport="true"
      :disabled="isLoading"
    >
      <template #trigger>
        <div class="quick_action_item">
          <ThemeImage class="size-5" :icon="imageNames.EMAIL_GREY" alt="email_icon" :style="isLoading ? { animation: 'pulse 1.5s infinite' } : {}" />
        </div>
      </template>

      <template #default>
        <div class="flex flex-col gap-2 p-2">
          <span class="text-[13px] secondary_color">Email</span>

          <div v-if="notFound" class="flex flex-row items-center gap-1">
            <ThemeImage :icon="imageNames.ERROR" alt="error_icon" />
            <span class="text-sm text_color">Not available</span>
          </div>

          <div class="flex flex-col gap-2">
            <span v-if="!notFound" class="text-2xl font-semibold text_color">************</span>
            <a-button
              v-if="!notFound"
              class="primary_medium min-w-48"
              @click="accessEmail"
              :loading="isEnriching"
            >
              <ThemeImage
                v-if="!isEnriching"
                :icon="imageNames.ENRICH_SEARCH"
                alt="enrich_search_icon"
                class="size-4"
              />
              {{ buttonText }}
            </a-button>
          </div>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { enrichWithApollo } from "@/src/utils/enrichUtils.js";
import { handleApiError } from "@/src/utils/errorUtils.js";
import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { quickActionsTypes } from "@/src/constants/componentConstants.js";
import { message } from "ant-design-vue";
import { entityTypes } from "@/src/constants/entityTypeConstants";
import { imageNames } from "@/src/constants/imageConstants.js";
import { showAlreadyExistsMessage, isProspectV2Enabled } from "@/src/utils/utils.js";
import { ENRICH_FIELDS } from "@/src/constants/prospectConstants.js";
import { enrichProspectEntity as enrichProspectEntityUtil } from "@/src/utils/prospectEnrichmentUtils.js";

const props = defineProps({
  row: { type: Object, required: true },
  action: { type: Object, required: true },
  fromProspect: { type: Boolean, required: false, default: false },
  source: { type: String, required: true },
  showEmailInRow: { type: Boolean, required: false, default: false },
});

const buttonText = computed(() => {
  return isEnriching.value ? "Accessing..." : "Access Email";
});

const emit = defineEmits(["action"]);

const entity = ref(null);
const dropdownRef = ref(null);
const notFound = ref(false);
const isLoading = ref(false);
const isEnriching = ref(false);
const actionKey = ref(props.action?.key || "email");

const enrichingEventName = computed(() => `email-enriching-${props.row.id}`);
const enrichmentRequestId = ref(null);

const hasValidEmail = computed(() => {
  const email = entity.value?.[actionKey.value];
  return email && email !== "email_not_unlocked@domain.com";
});

const setEnrichingState = (state, requestId = null) => {
  isEnriching.value = state;
  if (requestId) {
    enrichmentRequestId.value = requestId;
  }
  if (!state) {
    enrichmentRequestId.value = null;
  }
  eventBus.emit(enrichingEventName.value, state);
};

const accessEmail = async () => {
  if (props.fromProspect) {
    await enrichProspectEntity();
  } else {
    await enrichCRMEntity();
  }
};

const sendEmail = async () => {
  const email = entity.value?.[actionKey.value] || entity.value?.email;
  if (!email) return;

  dropdownRef.value?.closeDropdown();
  
  const entityToEmit = { ...entity.value };
  if (!entityToEmit.email && email) {
    entityToEmit.email = email;
  }
  
  emit("action", entityToEmit);
};

const enrichProspectEntity = async () => {
  setEnrichingState(true);
  const entityType = props.source === entityTypes.COMPANY ? entityTypes.COMPANY : entityTypes.LEAD;
  const payload = { ids: [entity.value?.id], enrich_fields: [ENRICH_FIELDS.EMAIL] };

  try {
    const response = await enrichProspectEntityUtil(payload, entityType);

    if (isProspectV2Enabled() && response?.request_id) {
      setEnrichingState(true, response.request_id);
      eventBus.emit(`track-enrichment-${entity.value?.id}`, { requestId: response.request_id, enrichField: ENRICH_FIELDS.EMAIL });
      return;
    }

    setEnrichingState(false);
    const leadData = response?.lead;
    const email = leadData?.email || leadData?.work_emails?.[0]?.email;
    if (email) {
      entity.value.email = email;
      entity.value[actionKey.value] = email;
      if (leadData.work_emails?.length) entity.value.work_emails = leadData.work_emails;
      if (leadData.personal_emails?.length) entity.value.personal_emails = leadData.personal_emails;
      eventBus.emit("table-row-updated", { email, work_emails: entity.value.work_emails, personal_emails: entity.value.personal_emails, action: quickActionsTypes.EMAIL, id: props.row.id });
      if (!props.showEmailInRow) sendEmail();
    } else if (response?.success === false) {
      showAlreadyExistsMessage({ customMessage: response?.message || response?.error });
    } else {
      notFound.value = true;
      message.info("No email found");
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => showAlreadyExistsMessage({ customMessage: errorMessage }));
    setEnrichingState(false);
  }
};

const enrichCRMEntity = async () => {
  setEnrichingState(true);
  let source = props.source;
  if (props.fromProspect) {
    source = props.row?.contact_id ? entityTypes.CONTACT : entityTypes.LEAD;
  }
  try {
    const entityId = entity.value?.lead_id || entity.value?.contact_id || entity.value?.id;
    const response = await enrichWithApollo(entityId, source, true, false, true);
    if (response?.email || response?.data?.email) {
      entity.value = response;
      if (entity.value && (response.email || response.data?.email)) {
        entity.value[actionKey.value] = response.email || response.data?.email;
      }
      eventBus.emit("table-row-updated", {
        ...response.data,
        action: quickActionsTypes.EMAIL,
        id: props.row.id,
      });
      if (!props.showEmailInRow) {
        sendEmail();
      }
    } else {
      notFound.value = true;
      message.info("No email found");
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  } finally {
    setEnrichingState(false);
  }
};

const handleEnrichmentCompleted = ({ entityId, enrichedData, status, enrichType, requestId }) => {
  if (
    entityId !== props.row?.id ||
    (enrichType && enrichType !== ENRICH_FIELDS.EMAIL) ||
    !isEnriching.value ||
    (enrichmentRequestId.value && requestId && enrichmentRequestId.value !== requestId)
  ) return;

  setEnrichingState(false);
  
  if (status === "completed" && enrichedData?.email) {
    entity.value.email = enrichedData.email;
    entity.value[actionKey.value] = enrichedData.email;
    
    if (enrichedData.work_emails?.length) {
      entity.value.work_emails = enrichedData.work_emails;
    }
    if (enrichedData.personal_emails?.length) {
      entity.value.personal_emails = enrichedData.personal_emails;
    }
    
    eventBus.emit("table-row-updated", {
      ...enrichedData,
      action: quickActionsTypes.EMAIL,
      id: props.row.id,
    });
    
    if (!props.showEmailInRow) {
      sendEmail();
    }
  } else {
    notFound.value = true;
    message.info("No email found");
  }
};

watch(
  () => props.row,
  (newRow) => {
    if (newRow) {
      entity.value = newRow;
      if (newRow.email && entity.value) {
        entity.value[actionKey.value] = newRow.email;
      }
    }
  },
  { deep: true }
);

watch(
  () => entity.value?.[actionKey.value],
  (newValue) => {
    if (newValue) {
      notFound.value = false;
    }
  }
);

onMounted(async () => {
  entity.value = props.row;
  
  eventBus.on(enrichingEventName.value, (state) => {
    isEnriching.value = state;
  });
  
  eventBus.on("prospect-enrichment-completed", handleEnrichmentCompleted);
  
  if (props.row && props.row.email && entity.value) {
    entity.value[actionKey.value] = props.row.email;
  }
});

onUnmounted(() => {
  eventBus.off(enrichingEventName.value);
  eventBus.off("prospect-enrichment-completed", handleEnrichmentCompleted);
});
</script>
